// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  averageValues,
} from "../core/math.js";
import {
  clampScore,
  elapsedAnchoredSampleForAnalysis,
  median,
  peerAverage,
} from "./utils.js";
import {
  thermalContextParameters,
} from "./detectors/thermal-context.js";

function round(value, digits = 1) {
  if (!Number.isFinite(value)) return null;
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function finiteValues(points, key) {
  return points.map((point) => point?.[key]).filter(Number.isFinite);
}

function channelAvailable(points, key) {
  return points.some((point) => Number.isFinite(point?.[key]));
}

function cylinderKeys(prefix, maxCylinder = 16) {
  return Array.from({ length: maxCylinder }, (_, index) => `${prefix}${index + 1}`);
}

function cylinderNumberFromKey(key) {
  const match = String(key ?? "").match(/^(?:cht|egt)(\d+)$/i);
  return match ? Number(match[1]) : null;
}

function bestSpeedKey(points) {
  return ["ias", "tas", "groundSpeed"].find((key) => channelAvailable(points, key)) ?? null;
}

function sampleForAnalysis(data, targetCount = thermalContextParameters.maxAnalysisSamples) {
  return elapsedAnchoredSampleForAnalysis(data, targetCount);
}

function sortedTimedPoints(data) {
  return sampleForAnalysis(data)
    .filter((point, index) => Number.isFinite(point?.elapsedSeconds ?? index))
    .map((point, index) => ({
      ...point,
      elapsedSeconds: Number.isFinite(point.elapsedSeconds) ? point.elapsedSeconds : index,
    }))
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
}

function segmentMedianChange(points, valueForPoint) {
  const values = points.map(valueForPoint).filter(Number.isFinite);
  if (values.length < 2) return { start: null, end: null, delta: null };
  const segmentSize = Math.max(1, Math.floor(values.length / 3));
  const start = median(values.slice(0, segmentSize));
  const end = median(values.slice(-segmentSize));
  return {
    start,
    end,
    delta: Number.isFinite(start) && Number.isFinite(end) ? end - start : null,
  };
}

function averageForKey(points, key) {
  return averageValues(finiteValues(points, key));
}

function deltaForKey(points, key) {
  return segmentMedianChange(points, (point) => point?.[key]).delta;
}

function dominantPhase(points) {
  const counts = new Map();
  points.forEach((point) => {
    const phase = String(point?.phase ?? "").trim();
    if (!phase) return;
    counts.set(phase, (counts.get(phase) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

function chtKeysForData(points) {
  const keys = cylinderKeys("cht").filter((key) => channelAvailable(points, key));
  if (keys.length) return keys;
  return channelAvailable(points, "cht") ? ["cht"] : [];
}

function egtKeyForChtKey(chtKey, points) {
  const cylinder = cylinderNumberFromKey(chtKey);
  const pairedKey = Number.isFinite(cylinder) ? `egt${cylinder}` : "egt";
  if (channelAvailable(points, pairedKey)) return pairedKey;
  return channelAvailable(points, "egt") ? "egt" : null;
}

function missingContextChannels(points) {
  const missing = [];
  if (!channelAvailable(points, "fuelFlow")) missing.push("fuelFlow");
  if (!channelAvailable(points, "rpm")) missing.push("rpm");
  if (!channelAvailable(points, "map")) missing.push("map");
  if (!bestSpeedKey(points)) missing.push("ias/tas/groundSpeed");
  if (!channelAvailable(points, "altitude")) missing.push("altitude");
  if (!channelAvailable(points, "verticalSpeed")) missing.push("verticalSpeed");
  return missing;
}

function precomputeEndIndicesByDuration(points, durations) {
  const elapsedSeconds = points.map((point) => point.elapsedSeconds);
  return durations.map((duration) => {
    const result = new Int32Array(points.length);
    let endIndex = 1;
    for (let startIndex = 0; startIndex < points.length; startIndex += 1) {
      const target = elapsedSeconds[startIndex] + duration;
      if (endIndex <= startIndex) endIndex = startIndex + 1;
      while (endIndex < points.length && elapsedSeconds[endIndex] < target) endIndex += 1;
      result[startIndex] = endIndex < points.length ? endIndex : -1;
    }
    return result;
  });
}

function contextTagsAndInterpretation(candidate, hasAirContext, speedKey) {
  const p = thermalContextParameters;
  const tags = [];
  const isRise = (candidate.chtDeltaF ?? 0) > 0;
  const isFall = (candidate.chtDeltaF ?? 0) < 0;
  const phase = String(candidate.phase ?? "");
  const climbContext = (candidate.altitudeDeltaFt ?? 0) >= p.climbAltitudeDeltaFt
    || (candidate.verticalSpeedAvgFpm ?? 0) >= p.climbVerticalSpeedFpm
    || /climb|takeoff/i.test(phase);
  const descentContext = (candidate.altitudeDeltaFt ?? 0) <= p.descentAltitudeDeltaFt
    || (candidate.verticalSpeedAvgFpm ?? 0) <= p.descentVerticalSpeedFpm
    || /descent|landing/i.test(phase);
  const reducedAirspeed = Number.isFinite(candidate.iasDeltaKt) && candidate.iasDeltaKt <= p.reducedAirspeedDeltaKt;
  const stableFuelFlow = Number.isFinite(candidate.fuelFlowDeltaGph) && candidate.fuelFlowDeltaGph >= p.stableFuelFlowDropGph;
  const increasedPower = [
    candidate.fuelFlowDeltaGph >= p.fuelFlowIncreaseGph,
    candidate.rpmDelta >= p.rpmIncrease,
    candidate.mapDelta >= p.mapIncreaseInHg,
    Number.isFinite(candidate.percentPowerDelta) && candidate.percentPowerDelta >= p.percentPowerIncrease,
  ].some(Boolean);
  const lowPower = [
    candidate.fuelFlowDeltaGph <= p.stableFuelFlowDropGph,
    candidate.rpmDelta <= p.rpmDecrease,
    candidate.mapDelta <= p.mapDecreaseInHg,
    Number.isFinite(candidate.percentPowerDelta) && candidate.percentPowerDelta <= p.percentPowerDecrease,
    Number.isFinite(candidate.percentPowerAvg) && candidate.percentPowerAvg <= p.lowPercentPower,
  ].some(Boolean);
  const isolated = Number.isFinite(candidate.peerChtDeltaF)
    && Number.isFinite(candidate.peerChtRateFpm)
    && (candidate.chtDeltaF - candidate.peerChtDeltaF >= p.isolatedCylinderDeltaF
      || candidate.chtRateFpm - candidate.peerChtRateFpm >= p.isolatedCylinderRateFpm);

  if (!hasAirContext) tags.push("missing-air-data");
  if (climbContext) tags.push("climb-context");
  if (reducedAirspeed) tags.push("reduced-airspeed");
  if (stableFuelFlow && isRise) tags.push("stable-fuel-flow");
  if (increasedPower) tags.push("increased-power");
  if (descentContext && isFall) tags.push("descent-cooling");
  if (lowPower) tags.push("low-power-context");
  if (Number.isFinite(candidate.percentPowerDelta) && candidate.percentPowerDelta <= p.percentPowerDecrease) tags.push("percent-power-reduction-context");
  if (Number.isFinite(candidate.percentPowerAvg) && candidate.percentPowerAvg <= p.lowPercentPower) tags.push("low-percent-power-context");
  if (isolated && isRise) tags.push("isolated-cylinder-rise");

  let interpretation = "not-context-explained";
  if (isRise && !hasAirContext && !increasedPower) {
    interpretation = "air-data-insufficient";
  } else if (isFall && descentContext) {
    interpretation = "low-power-cooling-context";
  } else if (isRise && climbContext && stableFuelFlow) {
    interpretation = "climb-load-cooling-context";
  } else if (isRise && increasedPower) {
    interpretation = "power-increase-context";
  }
  if (interpretation === "not-context-explained" && isRise) tags.push("unexplained-thermal-rise");

  return { tags, interpretation, speedKey };
}

function interpretationHint(candidate, tags, interpretation, speedKey) {
  if (interpretation === "climb-load-cooling-context") {
    const airspeedText = tags.includes("reduced-airspeed") ? "decreasing airspeed" : `${speedKey ?? "airspeed"} context`;
    return `CHT rise coincided with climb, positive vertical speed, ${airspeedText}, and stable fuel flow.`;
  }
  if (interpretation === "power-increase-context") return "CHT rise coincided with increased power/load context.";
  if (interpretation === "low-power-cooling-context") return "CHT cooling coincided with descent, landing, or reduced-power context.";
  if (interpretation === "air-data-insufficient") return "CHT movement is present, but air-data context is incomplete.";
  if (tags.includes("isolated-cylinder-rise")) return `Cylinder ${candidate.cylinder} rose more than its CHT peers without enough operating context to explain it.`;
  return "CHT rise was not explained by climb, airspeed, power, or fuel-flow context in this window.";
}

function severityForInterpretation(interpretation) {
  if (interpretation === "not-context-explained") return "watch";
  if (interpretation === "air-data-insufficient") return "watch";
  return "info";
}

function labelForWindow(candidate, interpretation) {
  const cylinderText = Number.isFinite(candidate.cylinder) ? `CHT${candidate.cylinder}` : "CHT";
  const labels = {
    "climb-load-cooling-context": "climb-context rise",
    "power-increase-context": "power-context rise",
    "low-power-cooling-context": "descent/low-power cooling",
    "air-data-insufficient": "context-limited movement",
    "not-context-explained": "unexplained rise",
  };
  return `${cylinderText} ${labels[interpretation] ?? "thermal movement"}`;
}

function candidateForWindow(points, window, chtKey, chtKeys, speedKey) {
  if (window.length < thermalContextParameters.minSamplesPerWindow) return null;
  const durationSeconds = window.at(-1).elapsedSeconds - window[0].elapsedSeconds;
  if (durationSeconds < thermalContextParameters.minWindowSeconds) return null;

  const chtChange = segmentMedianChange(window, (point) => point?.[chtKey]);
  if (!Number.isFinite(chtChange.delta)) return null;
  const chtRateFpm = chtChange.delta / durationSeconds * 60;
  const egtKey = egtKeyForChtKey(chtKey, points);
  const egtDeltaF = egtKey ? deltaForKey(window, egtKey) : null;
  const peerChange = segmentMedianChange(window, (point) => peerAverage(point, chtKeys, chtKey));
  const peerChtRateFpm = Number.isFinite(peerChange.delta) ? peerChange.delta / durationSeconds * 60 : null;
  const meaningfulRise = chtChange.delta >= thermalContextParameters.minChtRiseF
    || chtRateFpm >= thermalContextParameters.minChtRiseRateFpm;
  const meaningfulFall = chtChange.delta <= -thermalContextParameters.minChtFallF
    || chtRateFpm <= -thermalContextParameters.minChtFallRateFpm;
  const isolatedRise = Number.isFinite(peerChange.delta)
    && Number.isFinite(peerChtRateFpm)
    && (chtChange.delta - peerChange.delta >= thermalContextParameters.isolatedCylinderDeltaF
      || chtRateFpm - peerChtRateFpm >= thermalContextParameters.isolatedCylinderRateFpm);
  if (!meaningfulRise && !meaningfulFall && !isolatedRise) return null;

  const candidate = {
    startTime: window[0].elapsedSeconds,
    endTime: window.at(-1).elapsedSeconds,
    durationSeconds,
    phase: dominantPhase(window),
    cylinder: cylinderNumberFromKey(chtKey),
    channelKey: chtKey,
    chtStart: chtChange.start,
    chtEnd: chtChange.end,
    chtDeltaF: chtChange.delta,
    chtRateFpm,
    peerChtDeltaF: peerChange.delta,
    peerChtRateFpm,
    egtDeltaF,
    fuelFlowDeltaGph: deltaForKey(window, "fuelFlow"),
    fuelFlowAvgGph: averageForKey(window, "fuelFlow"),
    rpmDelta: deltaForKey(window, "rpm"),
    rpmAvg: averageForKey(window, "rpm"),
    mapDelta: deltaForKey(window, "map"),
    mapAvg: averageForKey(window, "map"),
    percentPowerDelta: deltaForKey(window, "percentPower"),
    percentPowerAvg: averageForKey(window, "percentPower"),
    iasDeltaKt: speedKey ? deltaForKey(window, speedKey) : null,
    iasAvgKt: speedKey ? averageForKey(window, speedKey) : null,
    altitudeDeltaFt: deltaForKey(window, "altitude"),
    verticalSpeedAvgFpm: averageForKey(window, "verticalSpeed"),
    oatAvgF: averageForKey(window, "oat"),
  };
  const hasAirContext = Boolean(speedKey)
    && (Number.isFinite(candidate.altitudeDeltaFt) || Number.isFinite(candidate.verticalSpeedAvgFpm));
  const { tags, interpretation } = contextTagsAndInterpretation(candidate, hasAirContext, speedKey);
  return {
    ...candidate,
    contextTags: tags,
    interpretation,
    severity: severityForInterpretation(interpretation),
    interpretationHint: interpretationHint(candidate, tags, interpretation, speedKey),
  };
}

const FIND_CANDIDATES_STRIDE = 15;

function findCandidates(points, chtKeys) {
  const speedKey = bestSpeedKey(points);
  const candidates = [];
  const endIndicesByDuration = precomputeEndIndicesByDuration(points, thermalContextParameters.windowSeconds);
  for (const chtKey of chtKeys) {
    for (let startIndex = 0; startIndex < points.length - 1; startIndex += FIND_CANDIDATES_STRIDE) {
      for (let durationIdx = 0; durationIdx < thermalContextParameters.windowSeconds.length; durationIdx += 1) {
        const endIndex = endIndicesByDuration[durationIdx][startIndex];
        if (endIndex < 0) continue;
        const window = points.slice(startIndex, endIndex + 1);
        const candidate = candidateForWindow(points, window, chtKey, chtKeys, speedKey);
        if (candidate) candidates.push(candidate);
      }
    }
  }
  return candidates;
}

function candidateRank(candidate) {
  const unexplainedBonus = candidate.interpretation === "not-context-explained" ? 100 : 0;
  const missingBonus = candidate.interpretation === "air-data-insufficient" ? 40 : 0;
  return unexplainedBonus + missingBonus + Math.abs(candidate.chtDeltaF ?? 0) + Math.abs(candidate.chtRateFpm ?? 0) * 2;
}

function clusteredCandidates(candidates) {
  const sorted = candidates.slice().sort((a, b) => candidateRank(b) - candidateRank(a));
  const selected = [];
  for (const candidate of sorted) {
    const overlaps = selected.some((item) => (
      item.channelKey === candidate.channelKey
      && candidate.startTime <= item.endTime
      && candidate.endTime >= item.startTime
    ));
    if (!overlaps) selected.push(candidate);
    if (selected.length >= thermalContextParameters.maxWindows) break;
  }
  return selected.sort((a, b) => a.startTime - b.startTime || String(a.channelKey).localeCompare(String(b.channelKey)));
}

function windowFromCandidate(candidate) {
  const keyTime = Math.round(candidate.startTime);
  const cylinderPart = Number.isFinite(candidate.cylinder) ? `cht${candidate.cylinder}` : "cht";
  return {
    key: `thermal-context-${cylinderPart}-${keyTime}-${Math.round(candidate.endTime)}`,
    label: labelForWindow(candidate, candidate.interpretation),
    severity: candidate.severity,
    startTime: round(candidate.startTime),
    endTime: round(candidate.endTime),
    phase: candidate.phase,
    sourceCylinder: Number.isFinite(candidate.cylinder) ? candidate.cylinder : null,
    channelKey: candidate.channelKey,
    interpretation: candidate.interpretation,
    contextTags: candidate.contextTags,
    stats: {
      chtDeltaF: round(candidate.chtDeltaF),
      chtRateFpm: round(candidate.chtRateFpm),
      fuelFlowDeltaGph: round(candidate.fuelFlowDeltaGph),
      iasDeltaKt: round(candidate.iasDeltaKt),
      altitudeDeltaFt: round(candidate.altitudeDeltaFt),
      verticalSpeedAvgFpm: round(candidate.verticalSpeedAvgFpm),
      rpmDelta: round(candidate.rpmDelta),
      mapDelta: round(candidate.mapDelta),
      percentPowerDelta: round(candidate.percentPowerDelta),
      percentPowerAvg: round(candidate.percentPowerAvg),
      oatAvgF: round(candidate.oatAvgF),
    },
    interpretationHint: candidate.interpretationHint,
  };
}

function scoreForWindows(windows) {
  if (!windows.length) return 100;
  const unexplained = windows.filter((window) => window.interpretation === "not-context-explained").length;
  const insufficient = windows.filter((window) => window.interpretation === "air-data-insufficient").length;
  const isolated = windows.filter((window) => window.contextTags.includes("isolated-cylinder-rise")).length;
  if (unexplained) return clampScore(82 - (unexplained - 1) * 6 - isolated * 4);
  if (insufficient) return clampScore(88 - (insufficient - 1) * 3);
  return windows.some((window) => window.interpretation === "climb-load-cooling-context" || window.interpretation === "power-increase-context")
    ? 96
    : 98;
}

function noteForWindows(windows, score) {
  if (!windows.length) return "No meaningful CHT thermal movement";
  const unexplained = windows.filter((window) => window.interpretation === "not-context-explained").length;
  if (unexplained) return `${unexplained} CHT rise window(s) need more context`;
  const insufficient = windows.filter((window) => window.interpretation === "air-data-insufficient").length;
  if (insufficient) return `${insufficient} thermal window(s) have incomplete air data`;
  return score >= 98 ? "Thermal movement is context-explained" : "CHT movement has climb or power context";
}

function statusForScore(score) {
  if (!Number.isFinite(score)) return null;
  if (score >= 88) return "Good";
  if (score >= 72) return "Watch";
  return "Review";
}

function primaryInterpretation(windows) {
  if (!windows.length) return null;
  const counts = new Map();
  windows.forEach((window) => counts.set(window.interpretation, (counts.get(window.interpretation) ?? 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

export function analyzeThermalContext(data = []) {
  const points = sortedTimedPoints(Array.isArray(data) ? data : []);
  const chtKeys = chtKeysForData(points);
  if (!chtKeys.length) {
    return {
      key: "thermalContext",
      score: null,
      note: "No CHT channel data",
      details: {
        windows: [],
        missingContextChannels: missingContextChannels(points),
        explainedWindowCount: 0,
        unexplainedWindowCount: 0,
        maxChtRateFpm: null,
        maxChtDeltaF: null,
        primaryInterpretation: null,
      },
    };
  }

  const windows = clusteredCandidates(findCandidates(points, chtKeys)).map(windowFromCandidate);
  const score = scoreForWindows(windows);
  const explainedWindowCount = windows.filter((window) => !["not-context-explained", "air-data-insufficient"].includes(window.interpretation)).length;
  const unexplainedWindowCount = windows.filter((window) => window.interpretation === "not-context-explained").length;
  return {
    key: "thermalContext",
    score,
    note: noteForWindows(windows, score),
    details: {
      windows,
      missingContextChannels: missingContextChannels(points),
      explainedWindowCount,
      unexplainedWindowCount,
      maxChtRateFpm: round(Math.max(...windows.map((window) => Math.abs(window.stats.chtRateFpm ?? 0)), 0)),
      maxChtDeltaF: round(Math.max(...windows.map((window) => Math.abs(window.stats.chtDeltaF ?? 0)), 0)),
      primaryInterpretation: primaryInterpretation(windows),
    },
  };
}
